import React, { useContext, useEffect, useRef } from "react";
import axios from "axios";
import { GlobalContext } from "../constants/Context";
import { logPlayCricketResponse } from "../utils/playCricketLogger";

function collectStatColumnNumbers(headings, values) {
  let maxColumn = 1;
  const consider = (source) => {
    if (!source || typeof source !== "object") return;
    for (let i = 2; i <= 40; i += 1) {
      if (`column_${i}` in source) {
        maxColumn = Math.max(maxColumn, i);
      }
    }
  };

  consider(headings);
  (values || []).forEach(consider);

  if (maxColumn < 2) return [];
  return Array.from({ length: maxColumn - 1 }, (_, index) => index + 2);
}

const LeagueTableAPI = ({ 
  divisionId, 
  onDataLoaded, 
  onError, 
  onLoading,
  competitionName,
}) => {
  const {API_TOKEN} = useContext(GlobalContext);
  const hasFetched = useRef(false);
  const onDataLoadedRef = useRef(onDataLoaded);
  const onErrorRef = useRef(onError);
  const onLoadingRef = useRef(onLoading);
  const competitionNameRef = useRef(competitionName);

  useEffect(() => {
    onDataLoadedRef.current = onDataLoaded;
    onErrorRef.current = onError;
    onLoadingRef.current = onLoading;
    competitionNameRef.current = competitionName;
  });

  useEffect(() => {
    hasFetched.current = false;
  }, [divisionId]);

  useEffect(() => {
    const fetchLeagueTable = async () => {
      if (!divisionId || divisionId === "undefined" || divisionId === "null") {
        onErrorRef.current("Valid Division ID is required");
        return;
      }

      if (hasFetched.current) {
        return;
      }

      const apiUrl = `https://play-cricket.com/api/v2/league_table.json?division_id=${divisionId}&api_token=${API_TOKEN}`;

      try {
        hasFetched.current = true;
        onLoadingRef.current(true);

        const response = await axios.get(apiUrl, {
          timeout: 15000,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        logPlayCricketResponse({
          endpoint: "league_table",
          requestUrl: apiUrl,
          httpStatus: response.status,
          isSuccess: true,
          rawResponse: response.data,
        });

        if (response.data.league_table && response.data.league_table.length > 0) {
          const leagueData = response.data.league_table[0];
          const headings = leagueData.headings || {};
          const values = leagueData.values || [];
          const displayName = competitionNameRef.current || leagueData.name;
          
          const formattedData = {
            leagueData,
            displayName,
            tableKey: leagueData.key,
            tableHeadings: headings,
            filteredTeams: values,
            statColumnNumbers: collectStatColumnNumbers(headings, values),
          };

          onDataLoadedRef.current(formattedData);
        } else {
          onErrorRef.current("No league table data found for this division");
        }
      } catch (error) {
        hasFetched.current = false;
        console.error("League Table API Error:", error);

        logPlayCricketResponse({
          endpoint: "league_table",
          requestUrl: apiUrl,
          httpStatus: error.response?.status || null,
          isSuccess: false,
          rawResponse: error.response?.data || null,
          errorMessage: error.message,
        });
        
        let errorMessage = "Failed to fetch league table";
        if (error.code === "ECONNABORTED") {
          errorMessage = "Request timeout - Please try again";
        } else if (error.response) {
          errorMessage = `Server error: ${error.response.status}`;
          if (error.response.status === 404) {
            errorMessage = `Division ID ${divisionId} not found`;
          }
        } else if (error.request) {
          errorMessage = "No response from server - Check network connection";
        }
        
        onErrorRef.current(errorMessage);
      } finally {
        onLoadingRef.current(false);
      }
    };

    if (divisionId && !hasFetched.current) {
      fetchLeagueTable();
    }
  }, [divisionId, API_TOKEN]);

  return null;
};

export default React.memo(LeagueTableAPI);
